import { ReactNode } from 'react'
import { motion } from 'framer-motion'
import Container from '@comp/ui/container'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import Text from '@comp/ui/text'
import Button from '@comp/ui/button'

const EASE = [0.22, 1, 0.36, 1] as const

interface Cta {
  label: string
  href: string
}

interface SolutionHeroProps {
  eyebrow: string
  title: ReactNode
  body: string
  primaryCta?: Cta
  secondaryCta?: Cta
  badges?: string[]
}

export default function SolutionHero({
  eyebrow,
  title,
  body,
  primaryCta = { label: 'Start free trial →', href: '/try/now' },
  secondaryCta = { label: 'Talk with our experts', href: '/contact' },
  badges,
}: SolutionHeroProps) {
  return (
    <section className="relative overflow-hidden bg-surface pt-20 pb-20 lg:pt-28 lg:pb-24">
      <BackgroundField />
      <Container size="xl" className="relative">
        <div className="max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
          >
            <Eyebrow tone="brand">{eyebrow}</Eyebrow>
          </motion.div>
          <motion.div
            className="mt-6"
            initial={{ y: 24 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.45, ease: EASE, delay: 0.05 }}
          >
            <Heading as="h1" size="display-xl">
              {title}
            </Heading>
          </motion.div>
          <motion.div
            className="mt-7 max-w-2xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: EASE, delay: 0.12 }}
          >
            <Text size="xl" tone="secondary">
              {body}
            </Text>
          </motion.div>
          <motion.div
            className="mt-9 flex flex-wrap items-center gap-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: EASE, delay: 0.2 }}
          >
            <Button href={primaryCta.href} variant="primary" size="lg">
              {primaryCta.label}
            </Button>
            <Button href={secondaryCta.href} variant="outline" size="lg">
              {secondaryCta.label}
            </Button>
          </motion.div>
          {badges && badges.length > 0 && (
            <motion.ul
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.35, ease: EASE, delay: 0.3 }}
              className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3"
            >
              {badges.map((b) => (
                <li
                  key={b}
                  className="inline-flex items-center gap-2 text-body-sm text-ink-tertiary"
                >
                  <CheckDot />
                  {b}
                </li>
              ))}
            </motion.ul>
          )}
        </div>
      </Container>
    </section>
  )
}

function BackgroundField() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0">
      <div className="absolute -top-32 -right-24 h-[560px] w-[560px] rounded-full bg-brand-100/70 blur-3xl" />
      <div className="absolute top-40 -left-32 h-[360px] w-[360px] rounded-full bg-accent-100/50 blur-3xl" />
      <svg
        className="absolute inset-0 h-full w-full text-brand-900/[0.04]"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <pattern
            id="grid-solution-hero"
            width="56"
            height="56"
            patternUnits="userSpaceOnUse"
          >
            <path d="M.5 56V.5H56" fill="none" stroke="currentColor" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid-solution-hero)" />
      </svg>
    </div>
  )
}

function CheckDot() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <circle cx="7" cy="7" r="7" fill="#25855c" fillOpacity="0.15" />
      <path
        d="M4 7.2 6.2 9.4 10 5.6"
        stroke="#25855c"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
